import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Layers,
  ShoppingCart,
  Banknote,
  Package,
  UserPlus,
  FolderTree,
  Settings,
  ChevronRight,
  ChevronLeft,
  DollarSign,
  CreditCard,
  FileText,
  LogOut,
  UserRound,
  History,
  CircleDot,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../../RTK/login/Authentication";
import { usePermissions } from "../../contexts/PermissionsContext";

const ModernSidebar = ({
  isCollapsed,
  setIsCollapsed,
  isMobileView,
  toggleNav,
  setToggleNav,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const Dispatch = useDispatch();
  const { hasPermission } = usePermissions(); // ✅ استخدام hook الصلاحيات

  // ✅ تعريف الصفحات مع صلاحياتها
  const navigationGroups = {
    main: [
      {
        name: "Dashboard",
        arabicName: "لوحة التحكم",
        link: "/",
        icon: LayoutDashboard,
        color: "blue",
        permission: "view_dashboard_stats",
      },
      {
        name: "Manage Users",
        arabicName: "المستخدمين",
        link: "/user",
        icon: Users,
        color: "green",
        permission: "view_all_users",
      },
      {
        name: "User Levels",
        arabicName: "المستويات",
        link: "/user-levels",
        icon: Layers,
        color: "purple",
        permission: "view_all_users",
      },
      {
        name: "ExpensesSummary",
        arabicName: "المصروفات",
        link: "/expenses-summary",
        icon: FileText,
        color: "red",
        permission: "view_expenses",
      },
    ],
    orders: [
      {
        name: "Manage Orders",
        arabicName: "الطلبات",
        link: "/order",
        icon: ShoppingCart,
        color: "yellow",
        permission: "view_all_orders",
      },
      {
        name: "Transactions",
        arabicName: "المعاملات",
        link: "/Transaction",
        icon: Banknote,
        color: "emerald",
        permission: "view_all_transactions",
      },
    ],
    finance: [
      {
        name: "ProfitMargin",
        arabicName: "التقرير المالى",
        link: "/profit-margin",
        icon: DollarSign,
        color: "teal",
        permission: "view_profit_reports",
      },
      {
        name: "PaymentMethods",
        arabicName: "طرق الدفع",
        link: "/Payment-methods",
        icon: CreditCard,
        color: "indigo",
        permission: "view_payment_methods",
      },
    ],
    inventory: [
      {
        name: "Manage Products",
        arabicName: "المنتجات",
        link: "/product",
        icon: Package,
        color: "orange",
        permission: "view_products",
      },
      {
        name: "Categories",
        arabicName: "التصنيفات",
        link: "/categories",
        icon: FolderTree,
        color: "pink",
        permission: "view_banners",
      },
      {
        name: "Employees",
        arabicName: "الموظفين",
        link: "/employees",
        icon: UserRound,
        color: "cyan",
        permission: null, // ✅ متاح للجميع
      },
      {
        name: "ActivityLogs",
        arabicName: "السجلات",
        link: "/activity-logs",
        icon: History,
        color: "violet",
        permission: "view_dashboard_stats",
      },
      {
        name: "Our Agents",
        arabicName: "وكلاؤنا",
        link: "/agent",
        icon: UserPlus,
        color: "lime",
        permission: "view_agents",
      },
      {
        name: "Settings",
        arabicName: "الإعدادات",
        link: "/settings",
        icon: Settings,
        color: "slate",
        permission: "view_website_settings",
      },
    ],
  };

  const colorClasses = {
    blue: {
      active: "from-blue-500 to-blue-600",
      hover: "hover:text-blue-400 hover:bg-blue-500/10",
      shadow: "shadow-blue-500/20",
    },
    green: {
      active: "from-green-500 to-green-600",
      hover: "hover:text-green-400 hover:bg-green-500/10",
      shadow: "shadow-green-500/20",
    },
    purple: {
      active: "from-purple-500 to-purple-600",
      hover: "hover:text-purple-400 hover:bg-purple-500/10",
      shadow: "shadow-purple-500/20",
    },
    red: {
      active: "from-red-500 to-red-600",
      hover: "hover:text-red-400 hover:bg-red-500/10",
      shadow: "shadow-red-500/20",
    },
    yellow: {
      active: "from-yellow-500 to-yellow-600",
      hover: "hover:text-yellow-400 hover:bg-yellow-500/10",
      shadow: "shadow-yellow-500/20",
    },
    emerald: {
      active: "from-emerald-500 to-emerald-600",
      hover: "hover:text-emerald-400 hover:bg-emerald-500/10",
      shadow: "shadow-emerald-500/20",
    },
    teal: {
      active: "from-teal-500 to-teal-600",
      hover: "hover:text-teal-400 hover:bg-teal-500/10",
      shadow: "shadow-teal-500/20",
    },
    indigo: {
      active: "from-indigo-500 to-indigo-600",
      hover: "hover:text-indigo-400 hover:bg-indigo-500/10",
      shadow: "shadow-indigo-500/20",
    },
    orange: {
      active: "from-orange-500 to-orange-600",
      hover: "hover:text-orange-400 hover:bg-orange-500/10",
      shadow: "shadow-orange-500/20",
    },
    pink: {
      active: "from-pink-500 to-pink-600",
      hover: "hover:text-pink-400 hover:bg-pink-500/10",
      shadow: "shadow-pink-500/20",
    },
    cyan: {
      active: "from-cyan-500 to-cyan-600",
      hover: "hover:text-cyan-400 hover:bg-cyan-500/10",
      shadow: "shadow-cyan-500/20",
    },
    violet: {
      active: "from-violet-500 to-violet-600",
      hover: "hover:text-violet-400 hover:bg-violet-500/10",
      shadow: "shadow-violet-500/20",
    },
    lime: {
      active: "from-lime-500 to-lime-600",
      hover: "hover:text-lime-400 hover:bg-lime-500/10",
      shadow: "shadow-lime-500/20",
    },
    slate: {
      active: "from-slate-500 to-slate-600",
      hover: "hover:text-slate-400 hover:bg-slate-500/10",
      shadow: "shadow-slate-500/20",
    },
  };

  // ✅ فلترة الصفحات حسب الصلاحيات
  const filterByPermission = (links) => {
    return links.filter((link) => {
      // لو مافيش صلاحية مطلوبة (null) يعني متاح للجميع
      if (link.permission === null) return true;
      // لو في صلاحية، نفحصها
      return hasPermission(link.permission);
    });
  };

  const NavLink = ({ link }) => {
    const isActive = location.pathname === link.link;
    const Icon = link.icon;
    const colors = colorClasses[link.color] || colorClasses.blue;

    return (
      <Link
        to={link.link}
        onClick={() => isMobileView && setToggleNav(false)}
        className={`relative flex items-center gap-3 p-3 rounded-xl transition-all duration-300 group overflow-hidden
          ${
            isActive
              ? `bg-gradient-to-r ${colors.active} text-white shadow-lg ${colors.shadow} scale-[1.02]`
              : `text-gray-400 hover:bg-gray-800/50 ${colors.hover}`
          }
        `}
      >
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full animate-pulse" />
        )}

        <div
          className={`relative p-2 rounded-lg transition-all duration-300 ${
            isActive
              ? "bg-white/20 backdrop-blur-sm"
              : "bg-gray-800/80 group-hover:bg-gray-800"
          }`}
        >
          <Icon
            className={`w-5 h-5 transition-transform duration-300 ${
              isActive ? "text-white scale-110" : "group-hover:scale-110"
            }`}
          />

          {isActive && (
            <CircleDot className="absolute -top-1 -right-1 w-3 h-3 text-white animate-ping" />
          )}
        </div>

        <span
          className={`${
            isCollapsed ? "hidden" : "block"
          } text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
            isActive ? "text-white" : ""
          }`}
        >
          {link.arabicName}
        </span>

        <div
          className={`absolute inset-0 bg-gradient-to-r ${colors.active} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-xl`}
        />

        {isCollapsed && (
          <div className="absolute right-full mr-4 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 invisible group-hover:visible whitespace-nowrap z-50 shadow-xl border border-gray-700">
            {link.arabicName}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 border-l border-b border-gray-700" />
          </div>
        )}
      </Link>
    );
  };

  const renderNavGroup = (title, links, isLast = false) => {
    // ✅ فلترة الصفحات حسب الصلاحيات
    const filteredLinks = filterByPermission(links);

    // ✅ لو مافيش صفحات متاحة، نخفي المجموعة كاملة
    if (filteredLinks.length === 0) return null;

    return (
      <div className="relative mb-6">
        {!isCollapsed && (
          <div className="flex items-center gap-2 px-3 mb-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent" />
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              {title}
            </h3>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent" />
          </div>
        )}

        {isCollapsed && (
          <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-3" />
        )}

        <div className="space-y-1">
          {filteredLinks.map((link, index) => (
            <NavLink key={index} link={link} />
          ))}
        </div>

        {!isLast && !isCollapsed && (
          <div className="mt-6 w-full h-px bg-gradient-to-r from-transparent via-yellow-500/20 to-transparent" />
        )}
      </div>
    );
  };

  const handleLogout = () => {
    Dispatch(logout());
    navigate("/login");
  };

  return (
    <>
      {toggleNav && isMobileView && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setToggleNav(false)}
        />
      )}

      <aside
        className={`fixed top-0 bottom-0 right-0 z-50 bg-gradient-to-b from-gray-900 via-gray-900 to-black border-l border-gray-800/50 transition-all duration-300 ease-in-out
          ${isCollapsed ? "w-20" : "w-64"}
          ${toggleNav || !isMobileView ? "translate-x-0" : "translate-x-full"}
          shadow-2xl shadow-black/50
        `}
        dir="rtl"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800/50 bg-gray-900/80 backdrop-blur-sm">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                <LayoutDashboard className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-white font-bold text-lg bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                لوحة التحكم
              </h1>
            </div>
          )}

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-yellow-500 hover:bg-gray-700/80 transition-all duration-300 hover:scale-110 active:scale-95"
          >
            {isCollapsed ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        <div className="h-[calc(100vh-8rem)] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-gray-600 p-3">
          {renderNavGroup("القائمة الرئيسية", navigationGroups.main)}
          {renderNavGroup("إدارة الطلبات", navigationGroups.orders)}
          {renderNavGroup("المالية", navigationGroups.finance)}
          {renderNavGroup(
            "المخزون والإعدادات",
            navigationGroups.inventory,
            true,
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-800/50 bg-gray-900/95 backdrop-blur-sm">
          <button
            onClick={handleLogout}
            className={`w-full relative flex items-center gap-3 p-3 rounded-xl transition-all duration-300 group overflow-hidden
              bg-gray-800/50 text-gray-400 hover:bg-red-500/10 hover:text-red-400 hover:scale-[1.02] active:scale-95`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-xl" />

            <div className="p-2 rounded-lg bg-gray-800/80 group-hover:bg-red-500/20 transition-all duration-300">
              <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
            </div>

            <span
              className={`${
                isCollapsed ? "hidden" : "block"
              } text-sm font-semibold whitespace-nowrap`}
            >
              تسجيل الخروج
            </span>

            {isCollapsed && (
              <div className="absolute right-full mr-4 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 invisible group-hover:visible whitespace-nowrap z-50 shadow-xl border border-gray-700">
                تسجيل الخروج
                <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 border-l border-b border-gray-700" />
              </div>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default ModernSidebar;
